import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import { Alert, Button, Card, Table } from '@themesberg/react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash, faFileInvoiceDollar } from '@fortawesome/free-solid-svg-icons';
import { API_URL, END_POINT_PO_SALES } from '../const';
import { hasUserPermission } from '../utils/userPermissions';
import CreateRecordModal from '../components/CreateRecordModal';
import PoSaleDetailSidebar from '../components/PoSaleDetailSidebar';
import PoSaleEditForm from './EditPoSale';

const readToken = () => sessionStorage.getItem('quotetoken')?.replace(/^"|"$/g, '');
const formatDate = value => value ? new Date(value).toLocaleDateString() : '-';
const formatAmount = value => '\u20b9 ' + Number(value || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 });

function PoSalesListing({ token }) {
  const [poSales, setPoSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [message, setMessage] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [editingId, setEditingId] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const deleting = useRef(false);
  const mounted = useRef(true);
  const location = useLocation();
  const openedFor = useRef(null);
  const openPoSaleId = location.state?.openPoSaleId;
  const canView = hasUserPermission('po_sales', 'view');
  const canEdit = hasUserPermission('po_sales', 'edit');
  const canDelete = hasUserPermission('po_sales', 'delete');

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  const isCurrent = () => mounted.current && readToken() === token;

  useEffect(() => {
    if (!canView) return undefined;
    let active = true;
    const controller = new AbortController();
    setLoading(true);
    setError('');
    axios.get(API_URL + END_POINT_PO_SALES, {
      headers: { Accept: 'application/json', Authorization: 'Bearer ' + token },
      signal: controller.signal,
    }).then(response => {
      if (!active || readToken() !== token) return;
      if (!response.data.status || !Array.isArray(response.data.data)) {
        throw new Error(response.data.message || 'Unable to load PO Sales records.');
      }
      setPoSales(response.data.data);
    }).catch(requestError => {
      if (active && readToken() === token) {
        setError(requestError.response?.data?.message || requestError.message || 'Unable to load PO Sales records.');
      }
    }).finally(() => { if (active && readToken() === token) setLoading(false); });
    return () => { active = false; controller.abort(); };
  }, [refreshKey, token, canView]);

  // Arriving from a Quote Details "PO Sales No." link: open that record once the list has loaded.
  useEffect(() => {
    if (!openPoSaleId || loading || openedFor.current === location.key) return;
    const match = poSales.find(record => String(record.id) === String(openPoSaleId));
    if (match) {
      openedFor.current = location.key; // open once per navigation, not again after later edits
      setSelectedId(match.id);
    }
  }, [openPoSaleId, location.key, loading, poSales]);

  const closeEditor = () => { if (!isUpdating) setEditingId(null); };
  const handleUpdated = result => {
    if (!isCurrent()) return;
    setEditingId(null);
    setMessage(result.message || 'PO Sales updated successfully.');
    if (result.data?.id) {
      setPoSales(current => current.map(record => record.id === result.data.id ? { ...record, ...result.data } : record));
    } else setRefreshKey(value => value + 1);
  };
  const handleDelete = async record => {
    if (!hasUserPermission('po_sales', 'delete') || deleting.current || !isCurrent()) return;
    deleting.current = true;
    setDeletingId(record.id);
    setActionError('');
    setMessage('');
    try {
      const confirmation = await Swal.fire({
        title: 'Delete PO Sales?', text: 'This will delete the PO Sales record for ' + (record.offer_no || record.system_quote_no || record.id) + '.',
        icon: 'warning', showCancelButton: true, confirmButtonText: 'Delete', cancelButtonText: 'Cancel',
      });
      if (!confirmation.isConfirmed || !isCurrent() || !hasUserPermission('po_sales', 'delete')) return;
      const response = await axios.delete(API_URL + END_POINT_PO_SALES + '/' + record.id, {
        headers: { Accept: 'application/json', Authorization: 'Bearer ' + token },
      });
      if (!isCurrent()) return;
      if (!response.data.status) throw new Error(response.data.message || 'Unable to delete PO Sales.');
      setPoSales(current => current.filter(item => item.id !== record.id));
      setSelectedId(current => current === record.id ? null : current);
      setMessage(response.data.message || 'PO Sales deleted successfully.');
    } catch (requestError) {
      if (isCurrent()) setActionError(requestError.response?.data?.message || requestError.message || 'Unable to delete PO Sales.');
    } finally {
      deleting.current = false;
      if (isCurrent()) setDeletingId(null);
    }
  };

  if (!canView) return <Alert variant="warning">You do not have permission to view PO Sales.</Alert>;

  const selectedIndex = poSales.findIndex(record => record.id === selectedId);
  const selected = selectedIndex >= 0 ? poSales[selectedIndex] : null;

  return (
    <>
      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-3">
        <h2 className="page-breadcrumb mb-0">PO Sales</h2>
      </div>
      {message && <Alert variant="success" dismissible onClose={() => setMessage('')}>{message}</Alert>}
      {actionError && <Alert variant="danger" dismissible onClose={() => setActionError('')}>{actionError}</Alert>}
      <Card border="light" className="shadow-sm">
        <Card.Body>
          {loading && <div className="text-center py-4" role="status">Loading PO Sales...</div>}
          {!loading && error && <Alert variant="danger">{error} <Button size="sm" variant="outline-danger" onClick={() => setRefreshKey(value => value + 1)}>Retry</Button></Alert>}
          {!loading && !error && poSales.length === 0 && <div className="text-center py-4">No PO Sales records found.</div>}
          {!loading && !error && poSales.length > 0 && (
            <Table hover responsive className="align-items-center table-flush">
              <thead className="thead-light"><tr>
                <th>PO Number</th><th>Quote No.</th><th>Customer</th>
                <th>PO Release Date</th><th>PO Amount</th><th>Payment Term</th><th>Status</th>
                {(canEdit || canDelete) && <th className="text-end">Actions</th>}
              </tr></thead>
              <tbody>{poSales.map(record => (
                <tr key={record.id} onClick={() => setSelectedId(record.id)}
                  style={{ cursor: 'pointer' }}
                  className={selectedId === record.id ? 'table-active' : ''}>
                  <td>{record.po_number || '-'}</td>
                  <td>{record.offer_no || record.system_quote_no || '-'}</td>
                  <td>{record.quote?.customer?.name || '-'}</td>
                  <td>{formatDate(record.po_release_date)}</td>
                  <td>{formatAmount(record.po_amount)}</td><td>{record.payment_term || '-'}</td><td>{record.status || '-'}</td>
                  {(canEdit || canDelete) && <td onClick={event => event.stopPropagation()}>
                    <div className="d-flex justify-content-end align-items-center gap-3 flex-nowrap">
                      {canEdit && <Button variant="link" className="p-0 text-success"
                        disabled={deletingId !== null} title="Edit PO Sales" aria-label={'Edit PO Sales ' + record.id}
                        onClick={() => { if (hasUserPermission('po_sales', 'edit')) { setMessage(''); setActionError(''); setEditingId(record.id); } }}>
                        <FontAwesomeIcon icon={faEdit} />
                      </Button>}
                      {canDelete && <Button variant="link" className="p-0 text-danger"
                        disabled={deletingId !== null} title="Delete PO Sales" aria-label={'Delete PO Sales ' + record.id}
                        onClick={() => handleDelete(record)}><FontAwesomeIcon icon={faTrash} /></Button>}
                    </div>
                  </td>}
                </tr>
              ))}</tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
      <CreateRecordModal show={editingId !== null && canEdit} onHide={closeEditor}
        isSubmitting={isUpdating} titleId="edit-po-sales-title" title="Edit PO Sales"
        description="Update purchase order details, payment terms, and status." icon={faFileInvoiceDollar} size="lg" dialogClassName="po-sales-modal-wide">
        {editingId !== null && canEdit && <PoSaleEditForm key={editingId} poSaleId={editingId}
          onSuccess={handleUpdated} onCancel={closeEditor} onSubmittingChange={setIsUpdating} />}
      </CreateRecordModal>
      <PoSaleDetailSidebar
        record={selected}
        show={Boolean(selected)}
        onClose={() => setSelectedId(null)}
        onEdit={id => { setMessage(''); setActionError(''); setEditingId(id); }}
        onDelete={handleDelete}
        canEdit={canEdit}
        canDelete={canDelete}
        deleting={deletingId !== null}
        onPrevious={() => selectedIndex > 0 && setSelectedId(poSales[selectedIndex - 1].id)}
        onNext={() => selectedIndex >= 0 && selectedIndex < poSales.length - 1 && setSelectedId(poSales[selectedIndex + 1].id)}
        hasPrevious={selectedIndex > 0}
        hasNext={selectedIndex >= 0 && selectedIndex < poSales.length - 1}
      />
    </>
  );
}

export default function PoSales() {
  const token = readToken();
  return <PoSalesListing key={token || 'signed-out'} token={token} />;
}
